import * as React from 'react';

import cx from 'classnames';
import { EventEmitter } from 'events';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';

import Box, { Props as BoxProps } from 'components/Box';
import Button, { ButtonColor } from 'components/ButtonDeprecated';
import Glyph from 'components/Glyph';
import withLayerAutoFocus from 'components/lib/withLayerAutoFocus';
import withLayerHideOnEscapeTrigger from 'components/lib/withLayerHideOnEscapeTrigger';
import withLayerHideOnTransitionTrigger from 'components/lib/withLayerHideOnTransitionTrigger';
import ScrollContext from 'components/ScrollContext';
import EventTracking, { getEventProperties } from 'lib/EventTracking';
import createChainedFunction from 'utils/createChainedFunction';

import { MouseEventHandler } from 'types/typescript/eventHandlers';

import './styles/Dialog.scss';

const stack: any[] = [];
// always have enough room for close button
const PADDING_TOP_MIN = 42;

type DialogSize = 'xlarge' | 'large' | 'medium' | 'small' | 'xsmall' | 'xxsmall';

const mapSizePropToClass: { [size: string]: string } = {
  xlarge: 'Dialog--xlarge',
  large: 'Dialog--large',
  medium: 'Dialog--medium',
  small: 'Dialog--small',
  xsmall: 'Dialog--xsmall',
  xxsmall: 'Dialog--xxsmall',
};

type DialogTheme = 'default' | 'dark' | 'light';

export type Props = {
  box?: boolean;
  children?: React.ReactNode;
  className?: string;
  eventProperties?: Object;
  eventScreenName?: string;
  eventKey?: string;
  full?: boolean;
  hideCloseButton?: boolean;
  hideOnBlur?: boolean;
  hideOnEscape?: boolean;
  hidePreviousButton?: boolean;
  size?: DialogSize;
  theme?: DialogTheme;
  minHeight?: number;
  onBackdropClick?: (event: React.MouseEvent) => void;
  onClick?: MouseEventHandler;
  onDialogDismiss?: Function;
  onRequestHide?: () => void;
  onRequestPrevious?: Function;
} & BoxProps;

type State = { paddingTop: number | undefined };

/**
 * ## Modal window
 * ### Dialogs are used to display content in a layer above the app.
 *
 * # Common usage
 *
 * ```example
 * <ModalTrigger
 *   modal={
 *     <Dialog
 *       title="Dialog title"
 *       subtext="Dialog subtext">
 *       <BoxBody>
 *         Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt
 *         ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation
 *         ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in
 *         reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur.
 *       </BoxBody>
 *     </Dialog>
 *   }>
 *   <Button>Show Dialog</Button>
 * </ModalTrigger>
 * ```
 *
 * # Required props
 * The Dialog must have either `onRequestHide` passed to it, or have `hideOnEscape={false}`,
 *
 * ```example
 * <ModalTrigger modal={<Dialog />}>
 *   <Button>Show Dialog</Button>
 * </ModalTrigger>
 * ```
 */

class Dialog extends React.Component<Props, State> {
  static contextTypes = {
    eventProperties: PropTypes.object,
  };

  static _emitter: EventEmitter = new EventEmitter();

  static getCurrent() {
    return stack[stack.length - 1];
  }

  static on(event: string, listener: () => void) {
    Dialog._emitter.addListener(event, listener);
  }

  static removeListener(event: string, listener: () => void) {
    Dialog._emitter.removeListener(event, listener);
  }

  state = {
    paddingTop: undefined,
  };

  componentDidMount() {
    const hideOnEscape = this.props.hideOnEscape || false;

    if (!(this.props.onRequestHide || !hideOnEscape)) {
      throw new Error('Dialog must have onRequestHide or hideOnEscape={false}');
    }

    stack.push(this);
    Dialog._emitter.emit('show');

    const { eventProperties, eventKey, eventScreenName, full } = this.props;

    if (eventScreenName) {
      const computedEventProperties = getEventProperties(eventProperties, this.context);
      EventTracking.view(eventScreenName, computedEventProperties);
    }

    if (eventKey) {
      EventTracking.trackKey(eventKey);
    }

    if (!full) {
      window.addEventListener('resize', this.resetPosition, false);
      this._dialogTimeout = window.setTimeout(() => this.resetPosition(), 0);
    }
  }

  componentWillUnmount() {
    const index = stack.indexOf(this);

    if (index >= 0) {
      stack.splice(index, 1);
    }

    Dialog._emitter.emit('hide');

    if (!this.props.full) {
      window.removeEventListener('resize', this.resetPosition, false);
      window.clearTimeout(this._dialogTimeout);
    }
  }

  closeDialog = (event: React.SyntheticEvent) => {
    const content = ReactDOM.findDOMNode(this.refs.content);

    // in IE only, content is somehow sometimes null
    // https://my.trackjs.com/messages/filtered?hash=c677c309de472679
    if (content && content.contains(event.target as HTMLElement)) {
      return;
    }

    event.stopPropagation();
    const { onDialogDismiss, onRequestHide } = this.props;

    if (onDialogDismiss) {
      onDialogDismiss();
    } else if (onRequestHide) {
      onRequestHide();
    }
  };

  _dialogTimeout: number | undefined = undefined;

  handleBackdropClick: MouseEventHandler = event => {
    if (this.props.hideOnBlur === false) {
      return;
    }

    if (event.target === event.currentTarget) {
      this.closeDialog(event);
    }
  };

  resetPosition = () => {
    if (this.props.full) {
      return;
    }

    const root = ReactDOM.findDOMNode(this) as HTMLElement | null;
    const container = ReactDOM.findDOMNode(this.refs.container) as HTMLElement | null;
    const offsetTop = root && container && (root.offsetHeight - container.offsetHeight) / 2;
    const paddingTop = offsetTop && offsetTop > PADDING_TOP_MIN ? offsetTop : PADDING_TOP_MIN;

    this.setState({ paddingTop });
  };

  renderContent() {
    const {
      box,
      children,
      full,
      hideCloseButton,
      hideOnBlur,
      hideOnEscape,
      hidePreviousButton,
      size,
      onDialogDismiss,
      onRequestHide,
      onRequestPrevious,
      ...props
    } = this.props;

    if (box !== false) {
      return (
        <Box {...props} onImageLoad={this.resetPosition} className="Dialog-box">
          {hidePreviousButton === false && (
            <Button
              aria-label="Back"
              color={ButtonColor.mascot}
              level="icon"
              className="Dialog-previousButton"
              eventElementName="Dialog-previousButton"
              onClick={(): void => {
                if (onRequestPrevious) {
                  onRequestPrevious();
                }
              }}>
              <Glyph name="arrow-left" size="large" solo />
            </Button>
          )}
          {children}
        </Box>
      );
    } else {
      return children;
    }
  }

  renderBody() {
    const { full, hideCloseButton, minHeight } = this.props;
    const { paddingTop } = this.state;

    if (full) {
      return this.renderContent();
    } else {
      return (
        <div style={paddingTop ? {} : { opacity: 0.01 }} className="Dialog-inner">
          <div style={{ paddingTop }} className="Dialog-wrap" onClick={this.handleBackdropClick}>
            <div
              ref="container"
              className="Dialog-container"
              onClick={e => e.stopPropagation()}
              style={minHeight ? { minHeight: `${minHeight}px` } : {}}>
              <div ref="content">{this.renderContent()}</div>
              {!hideCloseButton && (
                <Button
                  aria-label="Close dialog"
                  color={ButtonColor.whiteout}
                  level="icon"
                  className="Dialog-closeButton"
                  eventElementName="Dialog-closeButton"
                  onClick={this.closeDialog}>
                  <Glyph name="close" size="large" solo />
                </Button>
              )}
            </div>
          </div>
        </div>
      );
    }
  }

  render() {
    const { className, full, onClick, theme, size } = this.props;

    return (
      <ScrollContext
        tabIndex={0}
        className={cx('Dialog-backdrop', {
          'Dialog-backdrop--dark': theme === 'dark',
          'Dialog-backdrop--light': theme === 'light',
        })}
        onClick={createChainedFunction(onClick, this.handleBackdropClick) || undefined}
        data-test-selector="dialog">
        <div
          className={cx(
            'Dialog',
            size ? mapSizePropToClass[size] : mapSizePropToClass['medium'],
            {
              'Dialog--full': full,
            },
            className
          )}>
          {this.renderBody()}
        </div>
      </ScrollContext>
    );
  }
}

export { Dialog };
export default withLayerAutoFocus<Props>(
  withLayerHideOnTransitionTrigger(withLayerHideOnEscapeTrigger(Dialog))
);
